import { useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatTierPrice, tierCta, tierFeatures, tierInterval, tierIsFeatured } from '@/lib/pricing-tier';

interface PricingTiersProps {
  section: any;
}

export default function PricingTiers({ section }: PricingTiersProps) {
  const tiers: any[] = section.content?.tiers ?? [];
  const showToggle: boolean = section.content?.showToggle ?? false;
  const [isAnnual, setIsAnnual] = useState(false);
  const headingId = `pricing-heading-${section.id}`;

  if (tiers.length === 0) return null;

  return (
    <section aria-labelledby={headingId} className="bg-cream">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {section.title && (
            <h2
              id={headingId}
              className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
            >
              {section.title}
            </h2>
          )}
          {section.subtitle && (
            <p className="mt-2 text-sage-600 max-w-xl mx-auto">
              {section.subtitle}
            </p>
          )}
          <div className="mt-4 flex items-center justify-center gap-3" aria-hidden="true">
            <div className="h-px w-12 bg-sage-300" />
            <Sparkles className="h-4 w-4 text-sage-400" />
            <div className="h-px w-12 bg-sage-300" />
          </div>
        </div>

        {/* Monthly / Annual toggle */}
        {showToggle && (
          <div className="flex items-center justify-center gap-3 mb-10">
            <span
              className={cn(
                'text-sm font-medium transition-colors',
                !isAnnual ? 'text-sage-900' : 'text-sage-500'
              )}
            >
              Monthly
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={isAnnual}
              onClick={() => setIsAnnual(!isAnnual)}
              className={cn(
                'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 focus-visible:ring-offset-2',
                isAnnual ? 'bg-sage-700' : 'bg-sage-300'
              )}
            >
              <span
                className={cn(
                  'pointer-events-none inline-block h-5 w-5 rounded-full bg-cta-fg shadow-sm transition-transform',
                  isAnnual ? 'translate-x-5' : 'translate-x-0'
                )}
              />
            </button>
            <span
              className={cn(
                'text-sm font-medium transition-colors',
                isAnnual ? 'text-sage-900' : 'text-sage-500'
              )}
            >
              Annual
            </span>
          </div>
        )}

        {/* Pricing cards */}
        <div
          className={`grid grid-cols-1 gap-8 sm:grid-cols-2 ${
            // Four tiers read as 2x2 then one row; three stay three across.
            tiers.length >= 4 ? 'xl:grid-cols-4' : 'lg:grid-cols-3'
          }`}
        >
          {tiers.map((tier: any, i: number) => {
            // Shared with /pricing: the wizard writes numeric prices, the admin
            // builder writes labels like "$39" or "Custom".
            const { label: priceLabel, isAmount } = formatTierPrice(tier.price);
            // Rows come from the wizard, the admin builder, and hand edits,
            // which name these fields differently — read them all.
            const featured = tierIsFeatured(tier);
            const cta = tierCta(tier);
            const interval = tierInterval(tier);
            return (
            <div
              key={i}
              className={cn(
                'relative rounded-xl bg-surface p-8 shadow-sm border flex flex-col',
                featured
                  ? 'border-sage-700 ring-2 ring-sage-700'
                  : 'border-sage-100'
              )}
            >
              {featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full bg-cta px-3 py-0.5 text-xs font-semibold text-cta-fg">
                  Most Popular
                </span>
              )}

              <h3 className="font-display text-lg font-semibold text-sage-900">
                {tier.name}
              </h3>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-4xl font-bold text-sage-900">
                  {priceLabel}
                </span>
                {isAmount && interval && (
                  <span className="text-sm text-sage-500">/{interval}</span>
                )}
              </div>

              {tier.note && (
                <p className="mt-3 rounded-lg bg-sage-50 px-3 py-2 text-xs font-medium leading-relaxed text-sage-700">
                  {tier.note}
                </p>
              )}

              <ul className="mt-6 space-y-3 flex-1" role="list">
                {tierFeatures(tier.features).map((feature: string, fi: number) => (
                  <li key={fi} className="flex items-start gap-2 text-sm text-sage-700">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-sage-600" aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>

              {cta && (
                <a
                  href={cta.link}
                  className={cn(
                    'mt-8 block w-full rounded-lg px-4 py-2.5 text-center text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 focus-visible:ring-offset-2',
                    featured
                      ? 'bg-cta text-cta-fg hover:bg-cta-deep'
                      : 'bg-sage-100 text-sage-800 hover:bg-sage-200'
                  )}
                >
                  {cta.text}
                </a>
              )}
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
